/**
 * CodingBucket — E/M dropdown, G2211 smart toggle, ICD-10 search, Scan Note
 * + Additional Codes section (G-codes, AWV, CCM, preventive)
 * + CPT/HCPCS smart search
 * + Actionable validation flags
 */
import React from 'react';
import { api } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Check, Loader2, ChevronDown, AlertCircle, Plus, X, RefreshCw, Search, UserCheck, Info,
  Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import { Code2 } from 'lucide-react';
import { BucketHeader, EmptyBucket } from './shared';

// E/M code groups for the expanded picker
const EM_GROUPS = [
  {
    label: 'Office / Outpatient (Established)',
    codes: [
      { code: '99211', desc: 'Nurse visit — minimal' },
      { code: '99212', desc: 'Minimal complexity' },
      { code: '99213', desc: 'Low complexity' },
      { code: '99214', desc: 'Moderate complexity' },
      { code: '99215', desc: 'High complexity' },
    ],
  },
  {
    label: 'Office / Outpatient (New Patient)',
    codes: [
      { code: '99202', desc: 'Straightforward' },
      { code: '99203', desc: 'Low complexity' },
      { code: '99204', desc: 'Moderate complexity' },
      { code: '99205', desc: 'High complexity' },
    ],
  },
  {
    label: 'Annual Wellness / AWV',
    codes: [
      { code: 'G0402', desc: 'Welcome to Medicare (IPPE)' },
      { code: 'G0438', desc: 'AWV — first visit' },
      { code: 'G0439', desc: 'AWV — subsequent visit' },
    ],
  },
  {
    label: 'Preventive Medicine (Established)',
    codes: [
      { code: '99395', desc: 'Established, 18–39 years' },
      { code: '99396', desc: 'Established, 40–64 years' },
      { code: '99397', desc: 'Established, 65+ years' },
    ],
  },
  {
    label: 'Preventive Medicine (New Patient)',
    codes: [
      { code: '99385', desc: 'New patient, 18–39 years' },
      { code: '99386', desc: 'New patient, 40–64 years' },
      { code: '99387', desc: 'New patient, 65+ years' },
    ],
  },
  {
    label: 'Rural Health Clinic (RHC)',
    codes: [
      { code: 'G0466', desc: 'RHC — new patient' },
      { code: 'G0467', desc: 'RHC — established patient' },
      { code: 'G0468', desc: 'RHC — AWV' },
    ],
  },
];

function calcAge(dob) {
  if (!dob) return 0;
  const birth = new Date(dob);
  const now = new Date();
  let age = now.getFullYear() - birth.getFullYear();
  if (now.getMonth() < birth.getMonth() || (now.getMonth() === birth.getMonth() && now.getDate() < birth.getDate())) age--;
  return age;
}

export function CodingBucket({ enc, outputs, encounterId, fetchEncounter, setEncounter, patient, priorEncounterCount }) {
  const billing = outputs?.billing_suggestion;
  const isApproved = billing?.approval_status === 'approved' || billing?.approval_status === 'approve';

  const VALID_EM_CODES = ['99202','99203','99204','99205','99211','99212','99213','99214','99215'];
  const initialEM = (billing?.primary_code && (VALID_EM_CODES.includes(billing.primary_code) || billing.primary_code.startsWith('G') || billing.primary_code.startsWith('9939') || billing.primary_code.startsWith('9938') || billing.primary_code.startsWith('9937') || billing.primary_code.startsWith('9936')))
    ? billing.primary_code : '99213';

  const [selectedEM, setSelectedEM] = React.useState(initialEM);
  const [emOpen, setEmOpen] = React.useState(false);
  const [g2211, setG2211] = React.useState(billing?.g2211_addon || false);
  const [dxCodes, setDxCodes] = React.useState(billing?.diagnosis_codes || []);
  const [dxDescriptions, setDxDescriptions] = React.useState({});
  const [additionalCodes, setAdditionalCodes] = React.useState(billing?.additional_codes || []);
  const [additionalDescriptions, setAdditionalDescriptions] = React.useState({});
  const [showSearch, setShowSearch] = React.useState(false);
  const [showCptSearch, setShowCptSearch] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [cptSearchQuery, setCptSearchQuery] = React.useState('');
  const [searchResults, setSearchResults] = React.useState([]);
  const [cptResults, setCptResults] = React.useState([]);
  const [searchLoading, setSearchLoading] = React.useState(false);
  const [cptLoading, setCptLoading] = React.useState(false);
  const [scanLoading, setScanLoading] = React.useState(false);
  const [scanResults, setScanResults] = React.useState([]);
  const [showScanPicker, setShowScanPicker] = React.useState(false);
  const [selectedScan, setSelectedScan] = React.useState({});
  const [saving, setSaving] = React.useState(false);
  const [codeSuggestions, setCodeSuggestions] = React.useState([]);
  const [suggestionsLoaded, setSuggestionsLoaded] = React.useState(false);

  // Load procedure code suggestions (from AI rationale + validation flags)
  React.useEffect(() => {
    if (!encounterId || suggestionsLoaded) return;
    api.get(`/encounters/${encounterId}/suggest-additional-codes`).then(r => {
      setCodeSuggestions(r.data.suggestions || []);
      setSuggestionsLoaded(true);
    }).catch(() => setSuggestionsLoaded(true));
  }, [encounterId, suggestionsLoaded]);

  // G2211 auto-select: age >= 65 AND >= 2 prior encounters
  React.useEffect(() => {
    const age = calcAge(patient?.dob);
    if (age >= 65 && priorEncounterCount >= 2 && !billing?.g2211_addon) setG2211(true);
  }, [patient, priorEncounterCount, billing]);

  React.useEffect(() => {
    if (billing?.primary_code) {
      setSelectedEM(billing.primary_code);
    }
    if (billing?.diagnosis_codes) setDxCodes(billing.diagnosis_codes);
    if (billing?.g2211_addon !== undefined) setG2211(billing.g2211_addon);
    if (billing?.additional_codes) {
      setAdditionalCodes(billing.additional_codes);
      const descs = {};
      (billing.additional_codes || []).forEach(code => {
        if (!descs[code]) descs[code] = code;
      });
      setAdditionalDescriptions(descs);
    }
    // Fetch descriptions for pre-loaded dx codes (from AI pipeline)
    // so provider sees "E29.1 — Testicular hypofunction" not just "E29.1"
    const codesToLookUp = (billing?.diagnosis_codes || []).filter(
      c => c && !(billing?.diagnosis_code_descriptions || {})[c]
    );
    if (codesToLookUp.length > 0) {
      api.post('/icd10/batch-descriptions', { codes: codesToLookUp })
        .then(r => {
          const descMap = r.data.descriptions || {};
          setDxDescriptions(prev => ({ ...prev, ...descMap }));
        })
        .catch(() => {});
    }
    // Use any descriptions already in billing suggestion
    if (billing?.diagnosis_code_descriptions) {
      setDxDescriptions(prev => ({ ...prev, ...billing.diagnosis_code_descriptions }));
    }
  }, [billing]);

  const age = calcAge(patient?.dob);
  const g2211AutoApplied = age >= 65 && priorEncounterCount >= 2;

  const saveCoding = async (overrides = {}) => {
    setSaving(true);
    try {
      const res = await api.put(`/encounters/${encounterId}/coding`, {
        primary_code: overrides.em !== undefined ? overrides.em : selectedEM,
        g2211: overrides.g2211 !== undefined ? overrides.g2211 : g2211,
        diagnosis_codes: overrides.dxCodes !== undefined ? overrides.dxCodes : dxCodes,
        additional_codes: overrides.additionalCodes !== undefined ? overrides.additionalCodes : additionalCodes,
      });
      setEncounter(res.data.encounter);
    } catch { toast.error('Failed to save coding'); }
    finally { setSaving(false); }
  };

  const handleEMSelect = (code) => {
    setSelectedEM(code);
    setEmOpen(false);
    // G2211 only applies to standard E/M codes, not AWV/preventive
    const notEmCode = code.startsWith('G') || code.startsWith('99381') || code.startsWith('9939') || code.startsWith('9938');
    if (notEmCode && g2211) setG2211(false);
    saveCoding({ em: code, g2211: notEmCode ? false : g2211 });
  };

  const handleG2211Toggle = () => {
    const next = !g2211;
    setG2211(next);
    saveCoding({ g2211: next });
  };

  // ICD-10 search
  const handleSearchICD = async () => {
    if (!searchQuery.trim()) return;
    setSearchLoading(true);
    try { const res = await api.post('/icd10/search', { query: searchQuery }); setSearchResults(res.data.codes || []); }
    catch { toast.error('ICD search failed'); }
    finally { setSearchLoading(false); }
  };

  // CPT/HCPCS search
  const handleSearchCPT = async (q) => {
    const query = q !== undefined ? q : cptSearchQuery;
    if (!query.trim()) return;
    setCptLoading(true);
    try { const res = await api.post('/cpt/search', { query }); setCptResults(res.data.codes || []); }
    catch { toast.error('CPT search failed'); }
    finally { setCptLoading(false); }
  };

  const handleAddDx = (code, desc) => {
    if (dxCodes.includes(code)) return;
    const next = [...dxCodes, code];
    setDxCodes(next);
    if (desc) setDxDescriptions(prev => ({ ...prev, [code]: desc }));
    saveCoding({ dxCodes: next });
    setSearchResults([]); setSearchQuery(''); setShowSearch(false);
  };

  const handleRemoveDx = (code) => {
    const next = dxCodes.filter(c => c !== code);
    setDxCodes(next);
    saveCoding({ dxCodes: next });
  };

  const handleAddAdditionalCode = (code, desc) => {
    if (additionalCodes.includes(code)) { toast.info(`${code} already added`); return; }
    const next = [...additionalCodes, code];
    setAdditionalCodes(next);
    if (desc) setAdditionalDescriptions(prev => ({ ...prev, [code]: desc }));
    saveCoding({ additionalCodes: next });
    setCptResults([]); setCptSearchQuery(''); setShowCptSearch(false);
    toast.success(`${code} added to code set`);
  };

  const handleRemoveAdditional = (code) => {
    const next = additionalCodes.filter(c => c !== code);
    setAdditionalCodes(next);
    saveCoding({ additionalCodes: next });
  };

  const handleScanNote = async () => {
    setScanLoading(true);
    try {
      const res = await api.get(`/encounters/${encounterId}/scan-note-icd`);
      const codes = res.data.codes || [];
      setScanResults(codes);
      const pre = {};
      codes.forEach(c => { if (!dxCodes.includes(c.code)) pre[c.code] = true; });
      setSelectedScan(pre);
      setShowScanPicker(true);
    } catch { toast.error('Note scan failed'); }
    finally { setScanLoading(false); }
  };

  const handleApplyScan = () => {
    const toAdd = scanResults.filter(r => selectedScan[r.code] && !dxCodes.includes(r.code));
    const newDescs = {};
    toAdd.forEach(r => { newDescs[r.code] = r.description; });
    const next = [...dxCodes, ...toAdd.map(r => r.code)];
    setDxCodes(next);
    setDxDescriptions(prev => ({ ...prev, ...newDescs }));
    saveCoding({ dxCodes: next });
    setShowScanPicker(false);
    toast.success(`${toAdd.length} code(s) added`);
  };

  const handleApprove = async () => {
    try {
      await api.post(`/command-center/${encounterId}/approve`, { section: 'billing_suggestion', action: 'approve' });
      fetchEncounter();
      toast.success('Coding approved');
    } catch { toast.error('Failed'); }
  };

  if (!billing?.primary_code && !billing) return <EmptyBucket icon={Code2} message="No coding data — generate note first" />;

  // E/M description for current selection
  const allEmCodes = EM_GROUPS.flatMap(g => g.codes);
  const currentEmDesc = allEmCodes.find(c => c.code === selectedEM)?.desc || '';
  const isAWVOrPreventive = selectedEM && (selectedEM.startsWith('G04') || selectedEM.startsWith('9939') || selectedEM.startsWith('9938') || selectedEM.startsWith('9937') || selectedEM.startsWith('9936') || selectedEM.startsWith('G046'));

  return (
    <div className="space-y-4" data-testid="finalize-coding-bucket">
      <BucketHeader icon={Code2} title="Coding" status={isApproved ? 'complete' : 'pending'} />

      {/* ── E/M Code Row ── */}
      <div className="rounded-lg border p-4">
        <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-3">E/M Visit Code</p>
        <div className="flex items-center gap-3 flex-wrap">
          {/* E/M Dropdown — full group picker */}
          <div className="relative">
            <button
              onClick={() => setEmOpen(o => !o)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground font-mono font-bold text-lg hover:bg-primary/90 transition-colors"
              data-testid="em-code-selector">
              {selectedEM}
              <ChevronDown className={`w-4 h-4 transition-transform ${emOpen ? 'rotate-180' : ''}`} />
            </button>
            {emOpen && (
              <div className="absolute left-0 top-full mt-1 w-80 bg-card rounded-xl border shadow-2xl z-30 py-2 animate-in fade-in-0 slide-in-from-top-1 duration-100 max-h-[520px] overflow-y-auto">
                {EM_GROUPS.map(group => (
                  <div key={group.label}>
                    <p className="text-[8px] font-bold text-muted-foreground uppercase tracking-widest px-3 pt-2 pb-1 sticky top-0 bg-card border-b">
                      {group.label}
                    </p>
                    {group.codes.map(opt => (
                      <button key={opt.code} onClick={() => handleEMSelect(opt.code)}
                        className={`w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-accent transition-colors ${selectedEM === opt.code ? 'bg-primary/5' : ''}`}
                        data-testid={`em-option-${opt.code}`}>
                        <span className={`font-mono font-bold text-sm w-14 shrink-0 ${selectedEM === opt.code ? 'text-primary' : ''}`}>{opt.code}</span>
                        <span className="text-xs text-muted-foreground flex-1">{opt.desc}</span>
                        {selectedEM === opt.code && <Check className="w-3.5 h-3.5 text-primary shrink-0" />}
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* G2211 — only show for standard E/M codes */}
          {!isAWVOrPreventive && (
            <button
              onClick={handleG2211Toggle}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border font-mono text-sm font-semibold transition-all ${
                g2211 ? 'bg-emerald-50 border-emerald-300 text-emerald-700' : 'bg-muted border-border text-muted-foreground hover:border-foreground/30'
              }`}
              data-testid="g2211-toggle"
              title="Add-on: Longitudinal care complexity (Medicare)">
              {g2211 ? <Check className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
              G2211
            </button>
          )}
          {g2211 && g2211AutoApplied && !isAWVOrPreventive && (
            <span className="text-[10px] text-emerald-600 flex items-center gap-1">
              <UserCheck className="w-3 h-3" /> Auto: 65+, {priorEncounterCount} prior visits
            </span>
          )}
          {saving && <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />}
        </div>
        {currentEmDesc && <p className="text-[11px] text-muted-foreground mt-1.5">{currentEmDesc}</p>}
        {billing?.rationale && (
          <p className="text-[10px] text-muted-foreground mt-2 flex items-center gap-1">
            <Info className="w-3 h-3" /> AI: {billing.rationale}
          </p>
        )}
      </div>

      {/* ── Additional Procedure Codes (G-codes, AWV ancillaries, CCM, etc.) ── */}
      <div className="rounded-lg border p-4 space-y-3">
        <div className="flex items-center justify-between">
          <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Additional Codes</p>
          <Button variant="outline" size="sm" className="h-6 text-[10px] px-2 gap-1"
            onClick={() => setShowCptSearch(s => !s)} data-testid="add-procedure-code-btn">
            <Plus className="w-3 h-3" /> Search Codes
          </Button>
        </div>

        {/* AI-suggested codes from rationale (clickable) */}
        {codeSuggestions.length > 0 && additionalCodes.length === 0 && (
          <div className="space-y-1.5">
            <p className="text-[9px] text-violet-600 font-semibold flex items-center gap-1">
              <Sparkles className="w-3 h-3" /> AI detected in this visit — click to add:
            </p>
            {codeSuggestions.map(s => (
              <button key={s.code}
                onClick={() => handleAddAdditionalCode(s.code, s.description)}
                className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-left hover:bg-accent transition-colors text-xs"
                data-testid={`suggested-code-${s.code}`}>
                <Badge variant="outline" className="text-[9px] font-mono bg-violet-50 text-violet-700 border-violet-200 shrink-0">{s.code}</Badge>
                <span className="flex-1">{s.description}</span>
                <Plus className="w-3 h-3 text-muted-foreground" />
              </button>
            ))}
          </div>
        )}

        {/* Current additional codes */}
        {additionalCodes.length > 0 ? (
          <div className="space-y-1.5">
            {additionalCodes.map(code => {
              const desc = additionalDescriptions[code] || '';
              return (
                <div key={code} className="flex items-center gap-2 py-1 border-b border-dashed last:border-0" data-testid={`additional-code-${code}`}>
                  <Badge variant="outline" className="text-[10px] font-mono bg-sky-50 text-sky-700 border-sky-200 shrink-0">{code}</Badge>
                  <span className="text-xs flex-1 text-foreground">{desc || code}</span>
                  <button onClick={() => handleRemoveAdditional(code)}
                    className="p-0.5 rounded hover:bg-red-50 text-muted-foreground hover:text-red-500 transition-colors shrink-0"
                    data-testid={`remove-additional-${code}`}>
                    <X className="w-3 h-3" />
                  </button>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground/50 italic">No additional codes — search or accept AI suggestions above</p>
        )}

        {/* CPT/HCPCS Search */}
        {showCptSearch && (
          <div className="border-t pt-3 space-y-2 animate-in fade-in-0 slide-in-from-top-1 duration-100">
            <p className="text-[10px] font-semibold text-muted-foreground uppercase">CPT / HCPCS Search</p>
            <div className="flex gap-1.5">
              <input
                type="text"
                placeholder='e.g. "annual wellness", "G0439", "depression screening", "advance care planning"'
                value={cptSearchQuery}
                onChange={e => setCptSearchQuery(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleSearchCPT(); }}
                className="flex-1 text-xs border rounded-md px-2 py-1.5 bg-background focus:ring-1 focus:ring-primary/30"
                autoFocus
                data-testid="cpt-search-input"
              />
              <Button size="sm" className="h-8 text-[10px] px-3 gap-1" onClick={() => handleSearchCPT()} disabled={cptLoading}>
                {cptLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />}
                Search
              </Button>
            </div>
            {/* Quick picks */}
            <div className="flex gap-1.5 flex-wrap">
              {[
                { label: 'AWV', q: 'annual wellness visit' },
                { label: 'Depression', q: 'depression screening G-code' },
                { label: 'Advance Care', q: 'advance care planning' },
                { label: 'CCM', q: 'chronic care management' },
                { label: 'Tobacco', q: 'tobacco cessation counseling' },
              ].map(qp => (
                <button key={qp.label} onClick={() => { setCptSearchQuery(qp.q); handleSearchCPT(qp.q); }}
                  className="text-[9px] px-2 py-1 rounded-full bg-muted text-muted-foreground hover:text-foreground hover:bg-accent transition-colors font-medium border border-border"
                  data-testid={`cpt-quick-${qp.label.toLowerCase()}`}>
                  {qp.label}
                </button>
              ))}
            </div>
            {cptResults.length > 0 && (
              <div className="space-y-1 max-h-56 overflow-y-auto">
                {cptResults.map(r => (
                  <button key={r.code} onClick={() => handleAddAdditionalCode(r.code, r.description)}
                    disabled={additionalCodes.includes(r.code)}
                    className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-left transition-colors ${additionalCodes.includes(r.code) ? 'opacity-40 cursor-not-allowed' : 'hover:bg-accent'}`}
                    data-testid={`cpt-result-${r.code}`}>
                    <Badge variant="outline" className="text-[9px] font-mono shrink-0 bg-sky-50 text-sky-700 border-sky-200">{r.code}</Badge>
                    <span className="text-xs flex-1 text-left">{r.description}</span>
                    {additionalCodes.includes(r.code) ? <Check className="w-3 h-3 text-emerald-500 shrink-0" /> : <Plus className="w-3 h-3 text-muted-foreground shrink-0" />}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Diagnosis Codes ── */}
      <div className="rounded-lg border p-4 space-y-3">
        <div className="flex items-center justify-between">
          <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Diagnosis Codes</p>
          <div className="flex gap-1.5">
            <Button variant="outline" size="sm" className="h-6 text-[10px] px-2 gap-1" onClick={handleScanNote}
              disabled={scanLoading} data-testid="scan-note-btn">
              {scanLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
              Scan Note
            </Button>
            <Button variant="outline" size="sm" className="h-6 text-[10px] px-2 gap-1"
              onClick={() => setShowSearch(s => !s)} data-testid="add-icd-btn">
              <Plus className="w-3 h-3" /> Add
            </Button>
          </div>
        </div>
        <div className="space-y-1.5">
          {dxCodes.length === 0 && <p className="text-xs text-muted-foreground italic py-2">No diagnosis codes — use Search or Scan Note</p>}
          {dxCodes.map((code, i) => {
            const desc = dxDescriptions[code] || (billing?.diagnosis_code_descriptions || {})[code] || '';
            const problem = (enc?.note?.problem_oriented_ap || []).find(p => p.icd_code === code);
            return (
              <div key={code} className="flex items-center gap-2 py-1 border-b border-dashed last:border-0" data-testid={`dx-code-${code}`}>
                <span className="text-[10px] text-muted-foreground w-5 tabular-nums shrink-0">{i + 1}.</span>
                <Badge variant="outline" className="text-[10px] font-mono bg-amber-50 text-amber-700 border-amber-200 shrink-0">{code}</Badge>
                <span className="text-xs text-foreground flex-1 min-w-0 truncate">{desc || problem?.problem_name || ''}</span>
                <button onClick={() => handleRemoveDx(code)}
                  className="p-0.5 rounded hover:bg-red-50 text-muted-foreground hover:text-red-500 transition-colors shrink-0">
                  <X className="w-3 h-3" />
                </button>
              </div>
            );
          })}
        </div>

        {showSearch && (
          <div className="border-t pt-3 space-y-2 animate-in fade-in-0 slide-in-from-top-1 duration-100">
            <p className="text-[10px] font-semibold text-muted-foreground uppercase">ICD-10 Smart Search</p>
            <div className="flex gap-1.5">
              <input type="text" placeholder='e.g. "annual wellness", "sore throat", "diabetes"'
                value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleSearchICD(); }}
                className="flex-1 text-xs border rounded-md px-2 py-1.5 bg-background focus:ring-1 focus:ring-primary/30"
                autoFocus data-testid="icd-search-input" />
              <Button size="sm" className="h-8 text-[10px] px-3 gap-1" onClick={handleSearchICD} disabled={searchLoading}>
                {searchLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />}
                Search
              </Button>
            </div>
            {searchResults.length > 0 && (
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {searchResults.map(r => (
                  <button key={r.code} onClick={() => handleAddDx(r.code, r.description)}
                    disabled={dxCodes.includes(r.code)}
                    className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-left transition-colors ${dxCodes.includes(r.code) ? 'opacity-40 cursor-not-allowed' : 'hover:bg-accent'}`}
                    data-testid={`icd-result-${r.code}`}>
                    <Badge variant="outline" className="text-[9px] font-mono shrink-0 bg-amber-50 text-amber-700">{r.code}</Badge>
                    <span className="text-xs flex-1">{r.description}</span>
                    {dxCodes.includes(r.code) ? <Check className="w-3 h-3 text-emerald-500" /> : <Plus className="w-3 h-3 text-muted-foreground" />}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        {showScanPicker && scanResults.length > 0 && (
          <div className="border-t pt-3 space-y-2 animate-in fade-in-0 slide-in-from-top-1 duration-100">
            <div className="flex items-center justify-between">
              <p className="text-[10px] font-semibold text-muted-foreground uppercase">Found in Note — Select to Add</p>
              <button onClick={() => setShowScanPicker(false)} className="text-muted-foreground hover:text-foreground"><X className="w-3.5 h-3.5" /></button>
            </div>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {scanResults.map(r => (
                <label key={r.code} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-accent cursor-pointer">
                  <input type="checkbox" checked={!!selectedScan[r.code]}
                    onChange={e => setSelectedScan(prev => ({ ...prev, [r.code]: e.target.checked }))} className="w-3 h-3" />
                  <Badge variant="outline" className="text-[9px] font-mono shrink-0 bg-amber-50 text-amber-700">{r.code}</Badge>
                  <span className="text-xs flex-1">{r.description}</span>
                  {dxCodes.includes(r.code) && <Badge className="text-[8px] bg-emerald-50 text-emerald-600">In list</Badge>}
                </label>
              ))}
            </div>
            <Button size="sm" className="w-full text-xs gap-1" onClick={handleApplyScan}>
              <Plus className="w-3 h-3" /> Add Selected Codes
            </Button>
          </div>
        )}
      </div>

      {/* ── Validation Flags — Actionable ── */}
      {(billing?.validation_flags || []).length > 0 && (
        <div className="space-y-1.5">
          <p className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider">Validation Notes</p>
          {(billing.validation_flags || []).map((f, i) => {
            const flagStr = typeof f === 'string' ? f : f.message || '';
            const flagCode = typeof f === 'string' ? f : f.code || f.flag || '';
            // Map flag to suggested code
            const FLAG_CODE_MAP = {
              'verify_patient_eligibility_for_AWV': { code: 'G0439', label: 'Add G0439 (AWV Subsequent)' },
              'confirm_AWV_first_visit': { code: 'G0438', label: 'Add G0438 (AWV Initial)' },
              'confirm_depression_screening_completed': { code: 'G8510', label: 'Add G8510 (Depression Screen)' },
              'confirm_depression_screening_positive': { code: 'G8431', label: 'Add G8431 (Positive Screen)' },
              'validate_screening_counseling_documentation': { code: 'G0444', label: 'Add G0444 (Depression Counseling)' },
              'tobacco_counseling_documented': { code: '99406', label: 'Add 99406 (Tobacco Counseling)' },
              'advance_care_planning_discussed': { code: '99497', label: 'Add 99497 (Advance Care Planning)' },
            };
            const suggestion = FLAG_CODE_MAP[flagCode];
            return (
              <div key={i} className="flex items-start gap-2 py-1">
                <AlertCircle className="w-3 h-3 text-amber-500 shrink-0 mt-0.5" />
                <span className="text-[10px] text-amber-600 flex-1">{flagStr.replace(/_/g, ' ')}</span>
                {suggestion && !additionalCodes.includes(suggestion.code) && (
                  <button
                    onClick={() => {
                      const found = cptResults.find(r => r.code === suggestion.code);
                      api.post('/cpt/search', { query: suggestion.code }).then(r => {
                        const codeInfo = r.data.codes?.find(c => c.code === suggestion.code);
                        handleAddAdditionalCode(suggestion.code, codeInfo?.description || suggestion.label);
                      }).catch(() => handleAddAdditionalCode(suggestion.code, suggestion.label));
                    }}
                    className="text-[9px] font-medium text-primary hover:text-primary/80 whitespace-nowrap border border-primary/20 rounded px-1.5 py-0.5 hover:bg-primary/5 transition-colors shrink-0"
                    data-testid={`flag-add-code-${i}`}>
                    + {suggestion.label}
                  </button>
                )}
                {suggestion && additionalCodes.includes(suggestion.code) && (
                  <span className="text-[9px] text-emerald-600 flex items-center gap-0.5 shrink-0">
                    <Check className="w-3 h-3" /> Added
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Approve ── */}
      {!isApproved ? (
        <Button className="w-full text-xs gap-1.5" onClick={handleApprove} data-testid="finalize-approve-coding">
          <Check className="w-3.5 h-3.5" /> Approve Final Code Set
        </Button>
      ) : (
        <div className="flex items-center gap-2 text-xs text-emerald-700 py-2">
          <Check className="w-4 h-4" />
          <span>
            Coding approved — {selectedEM}{g2211 ? ' + G2211' : ''}{additionalCodes.length > 0 ? ` + ${additionalCodes.length} additional code${additionalCodes.length > 1 ? 's' : ''}` : ''} · {dxCodes.length} dx
          </span>
          <Button variant="ghost" size="sm" className="h-6 text-[10px] ml-auto"
            onClick={async () => { await api.post(`/command-center/${encounterId}/approve`, { section: 'billing_suggestion', action: 'unapprove' }).catch(() => {}); fetchEncounter(); }}>Edit</Button>
        </div>
      )}
    </div>
  );
}
